<template>
  <div v-rect-loading="loading" class="layout-template-edit">
    <div class="header">
      <div class="title">{{ $t(`${titleI18nKey}`) }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        ref="formEditor"
        :model="form"
        :formLines="formLines"
        :rules="formRules"
      >
        <template #content="{ line }">
          <h5 class="email-title">{{ $t(`${i18nPrefix}.label.emailHeader`) }}</h5>
          <TinyMceEditor
            v-if="showTinyMceEditor"
            v-model="form[line.props.headerKey]"
            class="rich-text-header"
            :disabled="isViewMode"
            :companyId="companyId"
            :rootCompanyId="rootCompanyId"
          >
          </TinyMceEditor>
          <h5 class="email-title">{{ $t(`${i18nPrefix}.label.emailFooter`) }}</h5>
          <TinyMceEditor
            v-if="showTinyMceEditor"
            v-model="form[line.props.footerKey]"
            class="rich-text-footer"
            :disabled="isViewMode"
            :companyId="companyId"
            :rootCompanyId="rootCompanyId"
          >
          </TinyMceEditor>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="toLayoutList">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" :disabled="isViewMode" @click="saveOrUpdateLayoutTemplate">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinyMceEditor from '../tinyMceEditor.vue'
import { validateEmail } from '@/utils/validator'

export default {
  components: {
    TinyMceEditor,
    WrappedFrom
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.notification',
      showTinyMceEditor: false,
      form: {},
      rootCompanyId: null,
      companyId: null
    }
  },
  computed: {
    titleI18nKey () {
      return this.$route.meta.titleI18nKey
    },
    layoutTemplateId () {
      return this.$route.params.layoutTemplateId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isViewMode () {
      return this.$route.meta.isViewMode
    },
    refForm () {
      return this.$refs.formEditor
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        name: {
          name: 'name',
          disabled: this.isViewMode,
          label: this.$t(`${this.i18nPrefix}.label.layoutName`),
          rules: { required: true, message: requiredMessage, trigger: 'blur' }
        },
        email_sender_address: {
          name: 'email_sender_address',
          disabled: this.isViewMode,
          label: this.$t(`${this.i18nPrefix}.label.emailSenderAddress`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' },
            { validator: validateEmail, trigger: ['blur', 'change'] }
          ]
        },
        sender_name_cn: {
          name: 'sender_name_cn',
          disabled: this.isViewMode,
          label: this.$t(`${this.i18nPrefix}.label.senderEmailCn`),
          rules: { required: true, message: requiredMessage, trigger: 'blur' }
        },
        sender_name_en: {
          name: 'sender_name_en',
          disabled: this.isViewMode,
          label: this.$t(`${this.i18nPrefix}.label.senderEmailEn`)
        },
        content_cn: {
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateCn`),
          contentSlot: 'content',
          props: {
            headerKey: 'content_header_cn',
            footerKey: 'content_footer_cn'
          }
        },
        content_en: {
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateEn`),
          contentSlot: 'content',
          props: {
            headerKey: 'content_header_en',
            footerKey: 'content_footer_en'
          }
        }
      }
    }
  },
  created () {
    this.loadTemplate()
  },
  methods: {
    ...mapActions('notificationTemplate', ['createLayoutTemplate', 'updateLayoutTemplate', 'getLayoutTemplateById']),
    async saveOrUpdateLayoutTemplate () {
      const params = {
        ...this.form,
        id: this.layoutTemplateId,
        systemTemplate: false
      }
      params.content_header_cn = this.nodeToHtml(this.form.content_header_cn, true)
      params.content_footer_cn = this.nodeToHtml(this.form.content_footer_cn, false)
      params.content_header_en = this.nodeToHtml(this.form.content_header_en, true)
      params.content_footer_en = this.nodeToHtml(this.form.content_footer_en, false)
      let result = false
      result = await this.refForm.submit()
      if (!result) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      let resp = null
      try {
        if (this.isEditMode) {
          resp = await this.updateLayoutTemplate(params)
        } else {
          resp = await this.createLayoutTemplate(params)
        }
        if (resp) {
          this.toLayoutList()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadTemplate () {
      this.loading = true
      try {
        if (this.layoutTemplateId) {
          const resp = await this.getLayoutTemplateById(this.layoutTemplateId)
          if (resp) {
            this.rootCompanyId = resp.root_company_id
            this.companyId = resp.company_id
            this.form = resp
            this.form.content_header_cn = this.removeNonEditableClass(this.form.content_header_cn, true)
            this.form.content_header_en = this.removeNonEditableClass(this.form.content_header_en, true)
            this.form.content_footer_cn = this.removeNonEditableClass(this.form.content_footer_cn, false)
            this.form.content_footer_en = this.removeNonEditableClass(this.form.content_footer_en, false)
          }
        }
        this.showTinyMceEditor = true
      } finally {
        this.loading = false
      }
    },
    removeNonEditableClass (value, checkHeader) {
      if (value !== null) {
        value = value.replace('class="mceNonEditable"', '')
        if (checkHeader) {
          value = value.replace('<email-header>', '')
          value = value.replace('</email-header>', '')
        } else {
          value = value.replace('<email-footer>', '')
          value = value.replace('</email-footer>', '')
        }
      }
      return value
    },
    nodeToHtml (oldValue, checkHeader) {
      if (oldValue == null) {
        oldValue = ''
      }
      let result = '<div class="mceNonEditable">' + oldValue + '</div>'
      if (checkHeader) {
        result = '<email-header>' + result + '</email-header>'
      } else {
        result = '<email-footer>' + result + '</email-footer>'
      }
      return result
    },
    toLayoutList () {
      this.$router.push({
        path: '/v-admin/notification-management/layout-template/list',
        query: this.$route.query
      })
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.showTinyMceEditor = false
        this.$nextTick(() => {
          this.showTinyMceEditor = true
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.layout-template-edit {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 980px;
    margin: 30px auto 0;

    .email-title {
      margin-top: 22px;
      position: absolute;
      left: -75px;
      color: #606266;
    }

    .rich-text-footer {
      margin-top: 22px;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}

.page-en {
  .layout-template-edit {
    .content {
      .email-title {
        left: -102px;
      }
    }
  }
}
</style>
